import { Box, Chip, LinearProgress, Skeleton, Stack, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Divider, IconButton } from '@mui/material';
import { OpenInFull as OpenInFullIcon, Close as CloseIcon } from '@mui/icons-material';
import React from 'react';
import { useLocation } from 'react-router-dom';
import type { WidgetProps } from '../types/index.js';
import { themesApi, type ThemeItem } from '../../../services/themes.js';

export const ThemeAnalysisWidget: React.FC<WidgetProps> = ({ widget }) => {
  const themes = widget.inputs.find(i => i.id === 'themes')?.value as any[] | undefined;
  const inputStock = widget.inputs.find(i => i.id === 'filterStock')?.value as any | undefined;
  const location = useLocation();
  const queryProfileId = React.useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return qs.get('profileId') || qs.get('ticker') || undefined;
  }, [location.search]);
  const derivedProfileId = React.useMemo(() => {
    const fromConfig = (widget.config as any)?.profileId as string | undefined;
    const fromInput = typeof inputStock === 'string' ? inputStock : inputStock?.symbol;
    return fromConfig || fromInput || queryProfileId;
  }, [widget.config, inputStock, queryProfileId]);
  const [fetchedThemes, setFetchedThemes] = React.useState<ThemeItem[] | null>(null);
  const [fetchedAt, setFetchedAt] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detail, setDetail] = React.useState<{
    theme: string;
    sentiment: string;
    summary: string;
    risks: Array<{ title: string; summary: string; significance?: number }>;
    trends: Array<{ title: string; summary: string; significance?: number }>;
    regulatory: Array<{ title: string; summary: string; significance?: number }>;
    lastUpdated?: string;
  } | null>(null);
  const [expandedOpen, setExpandedOpen] = React.useState(false);

  const analyzeTheme = async (theme: string) => {
    if (!derivedProfileId) return;
    try {
      setDetailLoading(true);
      setDetailOpen(true);
      const res = await themesApi.analyze(derivedProfileId, theme);
      setDetail(res);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    async function fetchIfNeeded() {
      if (themes && themes.length > 0) return; // inputs provided
      if (!derivedProfileId) return;
      try {
        setLoading(true);
        const data = await themesApi.discover(derivedProfileId);
        if (cancelled) return;
        setFetchedThemes(data.themes || []);
        setFetchedAt(data.lastUpdated || null);
      } catch {
        if (!cancelled) setFetchedThemes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchIfNeeded();
    return () => { cancelled = true; };
  }, [themes, derivedProfileId]);

  const items = themes ?? fetchedThemes ?? undefined;

  const classifySent = (s: any): 'pos' | 'neg' | 'neu' => {
    const str = String(s || 'neu').toLowerCase();
    return str.startsWith('pos') ? 'pos' : str.startsWith('neg') ? 'neg' : 'neu';
  };

  const summary = React.useMemo(() => {
    const s = { pos: 0, neu: 0, neg: 0 } as Record<'pos'|'neu'|'neg', number>;
    (items || []).forEach((t: any) => {
      const key = classifySent(t?.sentiment);
      s[key]++;
    });
    return s;
  }, [items]);

  if (!items) {
    return (
      <Box sx={{ p: 2 }}>
        {!derivedProfileId ? (
          <Typography variant="body2" color="text.secondary">
            No profile selected. Open a profile workspace to load themes.
          </Typography>
        ) : (
          <>
            <Skeleton width={140} />
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} width={280} />
            ))}
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ mr: 1 }}>Top Themes</Typography>
        <Chip size="small" label={`Pos ${summary.pos}`} color="success" variant="outlined" />
        <Chip size="small" label={`Neu ${summary.neu}`} variant="outlined" />
        <Chip size="small" label={`Neg ${summary.neg}`} color="error" variant="outlined" />
        <Box flexGrow={1} />
        {fetchedAt && (
          <Typography variant="caption" color="text.secondary">Updated {new Date(fetchedAt).toLocaleTimeString()}</Typography>
        )}
        <IconButton size="small" onClick={() => setExpandedOpen(true)} title="Expand">
          <OpenInFullIcon fontSize="small" />
        </IconButton>
      </Box>
      <Stack spacing={1}>
        {items.slice(0, 6).map((t: any, idx: number) => (
          <Box key={t?.theme ?? idx}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">{t.theme}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={t.sentiment || 'neutral'} variant="outlined" />
                <Button size="small" variant="text" onClick={() => analyzeTheme(t.theme)} disabled={!derivedProfileId}>Analyze</Button>
              </Stack>
            </Stack>
            <LinearProgress 
              variant="determinate" 
              value={Math.round(Math.max(0, Math.min(1, Number(t.relevance) || 0)) * 100)} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: classifySent(t?.sentiment) === 'pos' ? 'linear-gradient(90deg, #34C759, rgba(52,199,89,0.5))' : classifySent(t?.sentiment) === 'neg' ? 'linear-gradient(90deg, #FF3B30, rgba(255,59,48,0.5))' : 'linear-gradient(90deg, #8B66FF, rgba(139,102,255,0.4))',
                }
              }} 
            />
          </Box>
        ))}
      </Stack>

      {/* Expanded view dialog */}
      <Dialog open={expandedOpen} onClose={() => setExpandedOpen(false)} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Theme Explorer
          {fetchedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              • Updated {new Date(fetchedAt).toLocaleTimeString()}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setExpandedOpen(false)} size="small" title="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Stack spacing={1}>
            {items.map((t: any, idx: number) => (
              <Box key={`expanded-${t?.theme ?? idx}`}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{t.theme}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={t.sentiment || 'neutral'} variant="outlined" />
                    <Button size="small" variant="text" onClick={() => analyzeTheme(t.theme)} disabled={!derivedProfileId}>Analyze</Button>
                  </Stack>
                </Stack>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.round(Math.max(0, Math.min(1, Number(t.relevance) || 0)) * 100)} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      background: classifySent(t?.sentiment) === 'pos' ? 'linear-gradient(90deg, #34C759, rgba(52,199,89,0.5))' : classifySent(t?.sentiment) === 'neg' ? 'linear-gradient(90deg, #FF3B30, rgba(255,59,48,0.5))' : 'linear-gradient(90deg, #8B66FF, rgba(139,102,255,0.4))',
                    }
                  }} 
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpandedOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          {detail?.theme || 'Theme Analysis'}
          {detail?.lastUpdated && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              • Updated {new Date(detail.lastUpdated).toLocaleTimeString()}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && <Typography variant="body2">Analyzing...</Typography>}
          {!detailLoading && detail && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Summary</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{detail.summary || 'No summary'}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Risks</Typography>
              {(detail.risks || []).slice(0, 6).map((r, i) => (
                <Box key={`risk-${i}`} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{r.summary}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Trends</Typography>
              {(detail.trends || []).slice(0, 6).map((r, i) => (
                <Box key={`trend-${i}`} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{r.summary}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Regulatory</Typography>
              {(detail.regulatory || []).slice(0, 6).map((r, i) => (
                <Box key={`reg-${i}`} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{r.summary}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
